/**
 * Import v2 유틸리티
 *
 * CSV 다운로드, 헤더 검증, 매핑→레코드 변환, 수정값 반영 등
 */
import { normalizeToOtpauthUri } from 'services/totp'
import { FIELD_TYPES, FIELD_TYPE_CONFIG, DO_NOT_IMPORT, TITLE_FIELD } from './importReducer'

/**
 * CSV 헤더가 유효한지 검증 (첫 행이 헤더인지)
 * @param {string[]} headers - parseCsvRaw 결과의 headers
 * @returns {boolean}
 */
export const validateCsvHeaders = (headers) => {
  return Array.isArray(headers) && headers.length > 0
}

/**
 * 파일 이름이 허용 확장자 목록에 매치되는지 검증.
 *
 * `.json.enc`와 같은 복합 확장자는 `split('.').pop()`으로 판별 불가 → endsWith 사용.
 * 대소문자 무시.
 *
 * @param {string} fileName - 검사할 파일명 (경로 포함 가능)
 * @param {string[]} acceptedExts - 허용 확장자 목록 (각 항목은 '.'로 시작, 예: ['.json', '.json.enc'])
 * @returns {boolean}
 */
export const matchesAcceptedExt = (fileName, acceptedExts) => {
  if (!fileName || !Array.isArray(acceptedExts) || acceptedExts.length === 0) return false
  const lower = fileName.toLowerCase()
  return acceptedExts.some((ext) => typeof ext === 'string' && lower.endsWith(ext.toLowerCase()))
}

/**
 * PRESETS.acceptedExts를 HTML `<input type="file" accept>` 속성용 값으로 변환.
 *
 * 브라우저는 복합 확장자(`.json.enc`)를 인식하지 못하고 마지막 `.` 이후만
 * 확장자로 간주한다. `accept=".json,.json.enc"`를 넘기면 `.json.enc` 파일이
 * 파일 다이얼로그에서 비활성화되어 선택할 수 없다.
 *
 * 이 헬퍼는 각 확장자를 마지막 `.`부터의 부분만 남기고 중복을 제거해
 * 브라우저가 인식 가능한 단순 확장자 목록으로 변환한다.
 * 실제 파일 유효성 검증은 `matchesAcceptedExt`가 복합 확장자 그대로 수행.
 *
 * @param {string[]} acceptedExts - 예: ['.json', '.json.enc']
 * @returns {string} 쉼표로 구분된 단순 확장자 문자열 (예: '.json,.enc')
 */
export const toBrowserAccept = (acceptedExts) => {
  if (!Array.isArray(acceptedExts)) return ''
  const simplified = acceptedExts
    .filter((ext) => typeof ext === 'string' && ext.length > 0)
    .map((ext) => {
      const lastDot = ext.lastIndexOf('.')
      return lastDot >= 0 ? ext.substring(lastDot) : ext
    })
  return Array.from(new Set(simplified)).join(',')
}

/**
 * headers에 col이 존재하는지 확인하여 안전하게 매핑
 */
const safeCol = (headers, colName) => {
  return headers.includes(colName) ? colName : ''
}

/**
 * Google Password CSV 컬럼 ↔ Web Login 템플릿 label 매핑 (영문 label 기준)
 * 정책서 3.2.0의 자동 매핑 정의를 템플릿 label 기준으로 표현.
 */
export const GOOGLE_COLUMN_MAP = {
  Title: 'name',
  Website: 'url',
  ID: 'username',
  Password: 'password',
  Note: 'note',
}

/**
 * v2.12: CSV 컬럼명을 템플릿 translations 기준으로 현재 언어로 변환하는 함수를 만든다.
 *
 * /v1/resource-templates 응답의 translations 구조:
 *   { ko_KR: { "Title": "제목", "URL": "URL" }, en_US: {...}, ja_JP: {...} }
 *
 * 매칭 전략 (case-insensitive):
 *   1) csvCol 이 영문 label 자체와 일치 (e.g., "Title" → "제목")
 *   2) csvCol 이 임의 언어의 번역값과 일치 (e.g., "タイトル" → "제목")
 *   3) columnMap 이 있으면 csvHeader → 영문 label 역매핑 (Google CSV: "name" → "Title" → "제목")
 *
 * 일치 안 하면 원본 그대로 반환 (사용자 임의 컬럼명 보존).
 *
 * @param {Object} translations - schema.translations
 * @param {string} locale - 'ko_KR' | 'en_US' | 'ja_JP'
 * @param {Object|null} [columnMap] - { templateLabelEn: csvHeaderName } (e.g., GOOGLE_COLUMN_MAP)
 * @returns {(csvCol: string) => string}
 */
export const createCsvColumnTranslator = (translations, locale, columnMap = null) => {
  if (!translations || typeof translations !== 'object') {
    return (csvCol) => csvCol
  }
  const reverseMap = new Map()
  Object.values(translations).forEach((dict) => {
    if (!dict || typeof dict !== 'object') return
    Object.entries(dict).forEach(([enLabel, localized]) => {
      reverseMap.set(enLabel, enLabel)
      reverseMap.set(enLabel.toLowerCase(), enLabel)
      if (localized) {
        reverseMap.set(localized, enLabel)
        reverseMap.set(localized.toLowerCase(), enLabel)
      }
    })
  })
  if (columnMap && typeof columnMap === 'object') {
    Object.entries(columnMap).forEach(([enLabel, csvHeader]) => {
      if (!csvHeader) return
      reverseMap.set(csvHeader, enLabel)
      reverseMap.set(csvHeader.toLowerCase(), enLabel)
    })
  }
  const currentDict = translations[locale] || {}
  return (csvCol) => {
    if (!csvCol) return csvCol
    const enLabel = reverseMap.get(csvCol) || reverseMap.get(csvCol.toLowerCase())
    if (!enLabel) return csvCol
    return currentDict[enLabel] || csvCol
  }
}

/**
 * v2.5: Google 프리셋 CSV 헤더 검증.
 * GOOGLE_COLUMN_MAP의 값(실제 CSV 컬럼명) 중 최소 3개가 헤더에 포함되어야 Google CSV로 인정한다.
 * @param {string[]} headers - parseCsvRaw 결과의 headers
 * @returns {boolean}
 */
export const isGoogleCsvHeaders = (headers) => {
  if (!Array.isArray(headers)) return false
  const expected = Object.values(GOOGLE_COLUMN_MAP)
  const matched = expected.filter((col) => headers.includes(col)).length
  return matched >= 3
}

/**
 * 템플릿의 type → FIELD_TYPES internal ID 변환.
 *
 * 주의: 템플릿 스키마는 encrypt 정보를 개별 필드 레벨에서 제공하지 않아,
 * 'password' → PASSWORD/PIN, 'string' → TEXT/SECURE_TEXT 구분이 불가하다.
 * 대표 타입 하나로 매핑하며, 필요 시 사용자가 매핑 화면에서 변경 가능.
 */
const templateTypeToUiType = (type) => {
  const map = {
    string: FIELD_TYPES.TEXT,
    password: FIELD_TYPES.PASSWORD,
    email: FIELD_TYPES.EMAIL,
    url: FIELD_TYPES.URL,
    note: FIELD_TYPES.NOTE,
    date: FIELD_TYPES.DATE,
  }
  return map[type] || FIELD_TYPES.TEXT
}

/**
 * 템플릿 스키마로 컬럼 매핑을 초기화한다. (v2.3)
 *
 * @param {Object} schema - fetchTemplateSchema 결과 { templateName, translations, fields }
 * @param {string[]} csvHeaders - CSV 파싱 결과의 headers
 * @param {Object|null} [columnMap] - { templateLabelEn: csvHeaderName } (Google 자동 매핑용, null이면 매핑 없음)
 * @param {Array} [typeList] - v2.7: ResourceTemplateService.getAll()의 data_types. typeInfo 주입용
 * @returns {Array} 매핑 배열 (각 요소에 typeInfo 포함)
 */
export const createMappingsFromSchema = (schema, csvHeaders, columnMap = null, typeList = []) => {
  if (!schema || !Array.isArray(schema.fields)) return []

  return schema.fields
    .filter((f) => f.type !== 'files') // CSV는 파일 필드 표현 불가
    .map((f, idx) => {
      const typeInfo = typeList?.find?.((t) => t.type_name === f.type) || null
      return {
        id: idx + 1,
        uid: f.uid,
        field: f.labelLocalized, // UI 표시용 (로케일별 한글)
        fieldLabelEn: f.label, // API 전송용 (영문 원본)
        customLabel: null, // 기본 필드는 사용 안 함
        col: columnMap && columnMap[f.label] ? safeCol(csvHeaders, columnMap[f.label]) : '',
        required: f.required,
        type: templateTypeToUiType(f.type),
        typeInfo, // v2.7: { type_name, format, min_length, encrypt, ... } | null
        locked: false,
        isCustom: false,
        isEditingLabel: false,
        labelDraft: '',
      }
    })
}

/**
 * OfficePassword export 포맷 item의 type을 UI type(FIELD_TYPES)으로 복원. (v2.7)
 *
 * 실측: OfficePassword export의 item.type은 'string'으로 일반화되는 경우가 많음.
 * encrypt + typeInfo.format 조합으로 부분적 추정. 완전 복원은 템플릿 재조회 필요 (이번 범위 외).
 *
 * @param {string} itemType - item.type ('string'|'password'|'email'|'url'|'note'|'file'|'files')
 * @param {boolean|string} encrypt - item.encrypt (bool or 'True'/'False')
 * @param {string} [format] - typeList에서 조회한 typeInfo.format ('date'|'number'|...)
 * @returns {string} FIELD_TYPES 값
 */
export const itemTypeToUiType = (itemType, encrypt, format) => {
  const isEncrypt = encrypt === true || encrypt === 'True'
  if (itemType === 'password') return FIELD_TYPES.PASSWORD
  if (itemType === 'email') return isEncrypt ? FIELD_TYPES.SECURE_EMAIL : FIELD_TYPES.EMAIL
  if (itemType === 'url') return FIELD_TYPES.URL
  if (itemType === 'note') return isEncrypt ? FIELD_TYPES.SECURE_NOTE : FIELD_TYPES.NOTE
  if (itemType === 'files' || itemType === 'file') return FIELD_TYPES.TEXT // 파일은 content 검증 대상 아님
  if (itemType === 'totp') return FIELD_TYPES.TOTP // PR10: totp 정식 타입 승격(base32/URI 검증)
  if (itemType === 'string') {
    if (format === 'date') return FIELD_TYPES.DATE
    if (format === 'number') return FIELD_TYPES.PIN
    if (isEncrypt) return FIELD_TYPES.SECURE_TEXT
    return FIELD_TYPES.TEXT
  }
  return FIELD_TYPES.TEXT
}

/**
 * FIELD_TYPE_CONFIG에서 타입 ID로 itemType/encrypt 속성 조회
 */
const getFieldTypeConfig = (typeId) => FIELD_TYPE_CONFIG[typeId]

/**
 * v2 매핑 기반으로 CSV rows를 import records로 변환
 * @param {Object[]} rows - parseCsvRaw의 rows
 * @param {Array} mappings - 매핑 배열
 * @param {Function} [t] - i18next 번역 함수 (커스텀 필드 label 기본값용, 없으면 type ID 그대로)
 * @returns {Array} records - [{ title, items: [...] }]
 */
/**
 * totp 매핑 값의 저장용 URI 화(PR10) — bare secret 은 otpauth URI 로(등록명=레코드 title),
 * 이미 URI 면 원본 보존(파라미터 유지). 실패(깨진 base32)는 원문 유지 — 검증 스텝이
 * totpInvalid 에러 행으로 잡아 수정/제외를 유도한다(설계 확정 #4).
 * 호출 지점은 handleImportExecute 실행 직전 1곳 — 매핑 변환 시점에 URI 화하면 이후
 * 검증 화면의 제목 fix 가 이미 구워진 label 에 반영되지 않아 stale 해진다(PR #1543 리뷰).
 * String 캐스트: JSON 경로의 item.content 는 사용자 파일 유래라 숫자 등 비문자열일 수 있다.
 */
export const tryNormalizeTotp = (value, recordTitle) => {
  const raw = String(value || '').trim()
  if (!raw) return raw
  try {
    if (raw.toLowerCase().startsWith('otpauth://')) return normalizeToOtpauthUri(raw)
    return normalizeToOtpauthUri(raw, { account: String(recordTitle || '') })
  } catch (e) {
    return raw
  }
}

export const convertMappingToRecords = (rows, mappings, t = null) => {
  // 제목 매핑 찾기 (템플릿의 영문 label이 "Title"인 기본 필드)
  const titleMapping = mappings.find((m) => m.fieldLabelEn === 'Title')
  const titleCol = titleMapping ? titleMapping.col : null

  // v2.9: 기본 필드는 col 없어도 items에 빈 content로 포함 (편집 화면에서 필드 표시 보장)
  // v2.9.1: 커스텀 필드는 라벨 변경(customLabel) 또는 컬럼 매핑(col) 중 하나라도 있으면 포함
  //        → 사용자 의도 있는 필드만 포함, 실수로 추가한 빈 필드는 제외
  const includedMappings = mappings.filter((m) => {
    if (m === titleMapping) return false
    if (m.isCustom) {
      return !!m.customLabel || (!!m.col && m.col !== DO_NOT_IMPORT)
    }
    return true
  })

  return rows.map((row) => {
    // v2.9: Title fallback(URL 도메인 / 'Untitled') 제거. 빈값이면 validator가 requiredEmpty 에러 처리.
    const title = titleCol ? (row[titleCol] || '').trim() : ''

    // items 생성
    const items = []
    includedMappings.forEach((m) => {
      const hasColumn = m.col && m.col !== DO_NOT_IMPORT
      const value = hasColumn ? (row[m.col] || '').trim() : ''
      const config = getFieldTypeConfig(m.type)
      if (!config) return

      // 라벨 결정:
      //   - 기본 필드 (isCustom: false): localized label (기존 Record 생성과 일관성). v2.9.1 반전.
      //     fetchTemplateSchema의 labelLocalized는 번역 실패 시 영문 fallback이라 안전.
      //   - 커스텀 필드 (isCustom: true): customLabel → t(labelKey) → type ID 순서
      let label
      if (!m.isCustom) {
        label = m.field || m.fieldLabelEn
      } else if (m.customLabel) {
        label = m.customLabel
      } else if (t) {
        /* i18next-extract-disable-next-line */
        label = t(config.labelKey)
      } else {
        label = m.type
      }

      // PR10: totp 도 여기서는 원문 보존 — URI 화는 handleImportExecute 단일 지점(제목 fix 반영 후).
      // 검증은 bare secret 도 valid 로 통과시키므로(validateFieldValue totp) 흐름이 성립한다.
      items.push({
        type: config.itemType,
        uiType: m.type, // v2.7: validator 용 FIELD_TYPES 원본
        minLength: m.typeInfo?.min_length ?? null, // v2.7: validator 용
        maxLength: m.typeInfo?.max_length ?? null, // v2.9: maxLength 검증
        label,
        content: value,
        encrypt: config.encrypt,
        custom: m.isCustom,
        required: m.required || false,
        order: items.length + 1,
      })
    })

    return { title, items }
  })
}

/**
 * 수정값을 원본 record에 반영
 * @param {Array} records - 전체 record 배열
 * @param {Array} fixedErrors - action === 'fix'이고 fix.trim()이 있는 에러 배열
 * @returns {Array} 수정이 반영된 record 배열
 */
export const applyFixes = (records, fixedErrors) => {
  // row → record 인덱스 매핑 (row는 1-based)
  const cloned = records.map((r) => ({
    ...r,
    items: r.items ? r.items.map((item) => ({ ...item })) : [],
  }))

  fixedErrors.forEach((err) => {
    const recordIdx = err.row - 1
    if (recordIdx < 0 || recordIdx >= cloned.length) return

    const record = cloned[recordIdx]

    if (err.field === TITLE_FIELD) {
      record.title = err.fix.trim()
    } else {
      // items에서 label 매칭
      const item = record.items.find((it) => it.label === err.field)
      if (item) {
        item.content = err.fix.trim()
      }
    }
  })

  return cloned
}

/**
 * URL 필드에 프로토콜이 없으면 https://를 자동으로 추가한다.
 * 빈값은 그대로 둔다.
 * @param {Array} records - [{ title, items: [...] }]
 * @returns {Array} 정규화된 records (원본 변경 없이 복사본 반환)
 */
export const normalizeUrls = (records) => {
  return records.map((record) => ({
    ...record,
    items: record.items
      ? record.items.map((item) => {
          if (item.type === 'url' && item.content && String(item.content).trim()) {
            const trimmed = String(item.content).trim()
            if (!/^https?:\/\//i.test(trimmed)) {
              return { ...item, content: `https://${trimmed}` }
            }
          }
          return item
        })
      : [],
  }))
}

/**
 * 에러 행의 "필드" 표시 라벨 — 검증 테이블·제외 CSV 공용(노출 지점 2곳 동기).
 * duplicateTotp 는 인증 키 중복(레코드 단위 제외)인데 fix 파이프라인 재사용을 위해
 * 제목(TITLE_FIELD)에 앵커되어 있어, 그대로 표시하면 "제목이 중복"으로 오해된다 —
 * 표시만 인증 키로 치환한다. err.field 자체는 applyFixes 매칭 키라 변경 금지.
 */
export const getErrorFieldLabel = (err, t = null) => {
  /* i18next-extract-disable-next-line */
  const tr = (key, fallback) => (t ? t(key) : fallback)
  if (err.reason === 'duplicateTotp') return tr('record.totp.import.duplicateFieldLabel', '인증 키')
  if (err.field === TITLE_FIELD) return tr('record.import.v2.validation.titleField', '제목')
  return err.field
}

/**
 * 제외된 행의 원본 데이터 + 오류 사유를 CSV로 다운로드
 * @param {Array} excludedErrors - action === 'exclude'인 에러 배열
 * @param {Object[]} originalRows - 원본 행 데이터 (CSV rows 또는 JSON records)
 * @param {string[]} headers - 원본 CSV 헤더 (JSON이면 null)
 * @param {Function} [t] - i18next 번역 함수 (없으면 한국어 폴백)
 */
export const downloadExcludedCsv = (excludedErrors, originalRows, headers, t = null) => {
  if (!excludedErrors || excludedErrors.length === 0) return

  // i18next-extract가 래퍼 호출을 추적하지 못하므로 disable. 키들은 번역 JSON에 수동 관리.
  /* i18next-extract-disable-next-line */
  const tr = (key, fallback) => (t ? t(key) : fallback)

  const errorReasonMap = {
    requiredEmpty: tr(
      'record.import.v2.validation.error.requiredEmpty',
      '필수 필드가 비어있습니다',
    ),
    urlFormat: tr(
      'record.import.v2.validation.error.urlFormat',
      'http:// 또는 https:// 로 시작해야 합니다',
    ),
    dateFormat: tr('record.import.v2.validation.error.dateFormat', 'YYYY-MM-DD 형식이어야 합니다'),
    formatError: tr(
      'record.import.v2.validation.error.formatError',
      'YYYY-MM-DD 형식이어야 합니다',
    ),
  }

  const titleFieldLabel = tr('record.import.v2.validation.titleField', '제목')

  // 행 번호로 그룹핑 (같은 행에 여러 오류 가능)
  const rowErrorMap = {}
  excludedErrors.forEach((err) => {
    if (!rowErrorMap[err.row]) {
      rowErrorMap[err.row] = []
    }
    const fieldLabel = getErrorFieldLabel(err, t)
    rowErrorMap[err.row].push(`${fieldLabel}: ${errorReasonMap[err.reason] || err.reason}`)
  })

  // CSV 헤더 구성
  const reasonHeader = tr('record.import.v2.validation.csv.reasonHeader', '오류 사유')
  const rowNumHeader = tr('record.import.v2.validation.csv.rowNumHeader', '행번호')
  const csvHeaders = headers
    ? [...headers, reasonHeader]
    : [rowNumHeader, titleFieldLabel, reasonHeader]

  const csvRows = []

  Object.keys(rowErrorMap).forEach((rowNum) => {
    const idx = parseInt(rowNum, 10) - 1
    const reasons = rowErrorMap[rowNum].join(' | ')

    if (headers && originalRows[idx]) {
      // CSV 원본 행
      const rowData = headers.map((h) => {
        const val = originalRows[idx][h] || ''
        // CSV 이스케이프: 쉼표, 따옴표, 줄바꿈 포함 시 따옴표로 감싸기
        if (val.includes(',') || val.includes('"') || val.includes('\n')) {
          return `"${val.replace(/"/g, '""')}"`
        }
        return val
      })
      rowData.push(reasons.includes(',') ? `"${reasons}"` : reasons)
      csvRows.push(rowData.join(','))
    } else {
      // JSON 원본
      const record = originalRows[idx]
      const title = record ? record.title || '' : ''
      csvRows.push(
        `${rowNum},${title.includes(',') ? `"${title}"` : title},${reasons.includes(',') ? `"${reasons}"` : reasons}`,
      )
    }
  })

  const BOM = '\uFEFF'
  const csvContent = BOM + [csvHeaders.join(','), ...csvRows].join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const now = new Date()
  const timestamp = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
    '_',
    String(now.getHours()).padStart(2, '0'),
    String(now.getMinutes()).padStart(2, '0'),
    String(now.getSeconds()).padStart(2, '0'),
  ].join('')

  const a = document.createElement('a')
  a.href = url
  a.download = `excluded_items_${timestamp}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * 중복 매핑된 CSV 컬럼 목록 반환
 * @param {Array} mappings - 매핑 배열
 * @returns {string[]} 중복 컬럼명 배열
 */
export const getDuplicateColumns = (mappings) => {
  const usedCols = mappings.map((m) => m.col).filter((c) => c && c !== DO_NOT_IMPORT)
  return usedCols.filter((c, i) => usedCols.indexOf(c) !== i)
}
